import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { authService, userService } from '../services';
import apiClient from '../lib/api-client';
import type { User } from '../services/user.service';
import type { RegisterDto, LoginDto } from '../services/auth.service';

export interface AdoptedSession {
  user: User;
  accessToken: string;
  refreshToken: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (data: LoginDto) => Promise<void>;
  register: (data: RegisterDto) => Promise<void>;
  adoptSession: (session: AdoptedSession) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const userData = await userService.getProfile();
          setUser(userData);
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (data: LoginDto) => {
    const response = await authService.login(data);
    setUser(response.user as User);
    // Login response doesn't include statistics; fetch full profile
    try {
      const fullUser = await userService.getProfile();
      setUser(fullUser);
    } catch {
      // Non-critical: statistics will load on next page refresh
    }
  };

  const register = async (data: RegisterDto) => {
    const response = await authService.register(data);
    setUser(response.user as User);
  };

  /**
   * Adopt an already-minted token pair (for example the one returned by the
   * pass claim endpoint) instead of going through login/register. Persists the
   * tokens, sets the user immediately so guards/header react, then refreshes the
   * full profile in the background.
   */
  const adoptSession = async (session: AdoptedSession) => {
    apiClient.setTokens(session.accessToken, session.refreshToken);
    setUser(session.user);
    try {
      const fullUser = await userService.getProfile();
      setUser(fullUser);
    } catch {
      // Non-critical: the token-derived user is enough to authenticate.
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const refreshUser = async () => {
    const userData = await userService.getProfile();
    setUser(userData);
  };

  const value = {
    user,
    loading,
    login,
    register,
    adoptSession,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
